import { Routes, Route } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { CorporateSidebar } from "@/components/corporate/CorporateSidebar";
import { NPOSidebar } from "@/components/npo/NPOSidebar";
import { HostSidebar } from "@/components/host/HostSidebar";
import { AnonSidebar } from "@/components/dashboard/anon/AnonSidebar";
import SEO from "@/components/SEO";

// Import existing components
import NPOEvents from "@/pages/npo/NPOEvents";
import NPOHosts from "@/pages/npo/NPOHosts";
import NPOInvites from "@/pages/npo/NPOInvites";
import CreateCatalogEntry from "@/pages/npo/catalog/CreateCatalogEntry";
import CatalogDetailsPage from "@/pages/npo/catalog/CatalogDetailsPage";

// Import corporate dashboard components
import DashboardOverview from "@/pages/corporate/DashboardOverview";
import Events from "@/pages/corporate/Events";
import ProfilePage from "@/pages/profile/ProfilePage";
import HostsPage from "@/pages/corporate/HostsPage";
import { AskCora } from "@/pages/dashboard/cora";

// Import host dashboard components
import HostDashboardOverview from "@/pages/host/HostDashboardOverview";
import HostEvents from "@/pages/host/HostEvents";
import HostOrganizations from "@/pages/host/HostOrganizations";
import HostInvites from "@/pages/host/HostInvites";
import { RateCardManager } from "@/components/host/RateCardManager";
import PaymentsPage from "@/pages/payments/PaymentsPage";
import NPOHostDetails from "./npo/NPOHostDetails";
import { AnonDashboardContent } from "@/components/dashboard/anon/AnonDashboardContent";
import NPOSubscription from "./npo/NPOSubscription";
import NPOMicrosite from "./npo/NPOMicrosite";
import { GenerateImagePage } from "./npo/tools/GenerateImagePage";
import { WriteEmailPage } from "./npo/tools/WriteEmailPage";
import { SocialMediaPage } from "./npo/tools/SocialMediaPage";
import BrainstormIdeasPage from "./npo/tools/BrainstormIdeasPage";
import ChatPage from "./npo/tools/ChatPage";
import ChatLeadsPage from "./dashboard/tools/chat/leads";
import CampaignsPage from "./dashboard/CampaignsPage";
import CreateCommunityEvent from "./npo/community-events/CreateCommunityEvent";
import CommunityEvents from "./npo/community-events/CommunityEvents";
import CommunityEventDetails from "./npo/community-events/CommunityEventDetails";
import FundraisingAndGrantsPage from "./npo/ai-engine/FundraisingAndGrantsPage";
import VolunteerEngagementPage from "./npo/ai-engine/VolunteerEngagementPage";
import MarketingOutreachPage from "./npo/ai-engine/MarketingOutreachPage";
import ImpactReportsAndAnalyticsPage from "./npo/ai-engine/ImpactReportsAndAnalyticsPage";
import CommunityEventsVolunteersList from "./npo/community-events/CommunityEventsVolunteersList";
import SettingsPage from "./settings/SettingsPage";
import GraphPage from "./dashboard/knowledge/GraphPage";
import VolunteerRecruitmentEmailPage from "./npo/ai-engine/VolunteerRecruitmentEmailPage";
import VolunteerThankYouEmailPage from "./npo/ai-engine/VolunteerThankYouEmailPage";
import CommunityEventPromotionImagesPage from "./npo/ai-engine/CommunityEventPromotionImagesPage";
import VolunteerRecognitionPage from "./npo/ai-engine/VolunteerRecognitionPage";
import MissionHubContentPage from "./npo/mission-hub/MissionHubContentPage";
import EventDetails from "./events/EventDetails";
import { MyDrive } from "./knowledge/MyDrive";
import CreditsPage from "./dashboard/CreditsPage";
import NPODashboard from "./npo/NPODashboard";
import CorporatePartnershipsPage from "./dashboard/corporate-partnerships/CorporatePartnershipsPage";
import AIGrowthEnginePage from "./dashboard/ai-tools/AIGrowthEnginePage";
import AIAssistantPage from "./dashboard/chatbot/AIAssistantPage";
import ExperienceLibraryPage from "./dashboard/catalog/ExperienceLibraryPage";
import MyWebsitePage from "./dashboard/mission-hub/MyWebsitePage";
import CorporateCampaignsPage from "./corporate/CorporateCampaignsPage";
import CorporateNewCampaignsPage from "./corporate/CorporateNewCampaignsPage";
import CorporateEditCampaignsPage from "./corporate/CorporateEditCampaignsPage";
import NpoRfqPage from "@/pages/npo/NpoRfqPage";
import NpoRfqResponsePage from "./npo/NpoRfqResponsePage";
import CorporateViewCampaignsPage from "./corporate/CorporateViewCampaignsPage";
import OpportunitiesRateCard from "./npo/OpportunitiesRateCard";
import ManageUsers from "./ManageUsers";

const HostDashboardContent = ({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) => (
  <Routes>
    <Route
      index
      element={<HostDashboardOverview session={session} profile={profile} />}
    />
    <Route
      path="events"
      element={<HostEvents session={session} profile={profile} />}
    />
    <Route
      path="events/:id"
      element={<EventDetails session={session} profile={profile} />}
    />
    <Route
      path="invites"
      element={<HostInvites session={session} profile={profile} />}
    />
    <Route
      path="organizations"
      element={<HostOrganizations session={session} profile={profile} />}
    />
    <Route
      path="rate-cards"
      element={<RateCardManager session={session} profile={profile} />}
    />
    <Route
      path="payments"
      element={<PaymentsPage session={session} profile={profile} />}
    />
    <Route
      path="profile"
      element={<ProfilePage session={session} profile={profile} />}
    />
    <Route
      path="settings"
      element={<SettingsPage session={session} profile={profile} />}
    />
  </Routes>
);

const NPODashboardContent = ({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) => (
  <Routes>
    <Route
      index
      element={<NPODashboard session={session} profile={profile} />}
    />
    <Route
      path="users"
      element={<ManageUsers session={session} profile={profile} />}
    />
    <Route
      path="credits"
      element={<CreditsPage session={session} profile={profile} />}
    />
    <Route
      path="campaigns"
      element={<CampaignsPage session={session} profile={profile} />}
    />
    <Route
      path="community-events/create"
      element={<CreateCommunityEvent session={session} profile={profile} />}
    />
    <Route
      path="community-events/:slug"
      element={<CommunityEventDetails session={session} profile={profile} />}
    />
    <Route
      path="community-events/volunteers"
      element={
        <CommunityEventsVolunteersList session={session} profile={profile} />
      }
    />
    <Route
      path="events"
      element={<NPOEvents session={session} profile={profile} />}
    />
    <Route
      path="events/:id"
      element={<EventDetails session={session} profile={profile} />}
    />
    <Route
      path="events/hosts"
      element={<NPOHosts session={session} profile={profile} />}
    />
    <Route
      path="events/hosts/:id"
      element={<NPOHostDetails session={session} profile={profile} />}
    />
    <Route
      path="events/invites"
      element={<NPOInvites session={session} profile={profile} />}
    />
    <Route
      path="catalog"
      element={<ExperienceLibraryPage session={session} profile={profile} />}
    />
    <Route
      path="catalog/create"
      element={<CreateCatalogEntry session={session} profile={profile} />}
    />
    <Route
      path="catalog/:id"
      element={<CatalogDetailsPage session={session} profile={profile} />}
    />
    <Route
      path="rate-cards"
      element={<OpportunitiesRateCard session={session} profile={profile} />}
    />
    <Route
      path="community-events"
      element={<CommunityEvents session={session} profile={profile} />}
    />
    <Route
      path="community-events/create"
      element={<CreateCommunityEvent session={session} profile={profile} />}
    />
    <Route
      path="community-events/:slug/edit"
      element={<CreateCommunityEvent session={session} profile={profile} />}
    />
    <Route
      path="campaigns"
      element={<CampaignsPage session={session} profile={profile} />}
    />
    <Route
      path="corporate-events"
      element={
        <CorporatePartnershipsPage session={session} profile={profile} />
      }
    />
    <Route
      path="corporate-events/rfqs"
      element={<NpoRfqPage session={session} profile={profile} />}
    />
    <Route
      path="corporate-events/rfqs/:id"
      element={<NpoRfqResponsePage session={session} profile={profile} />}
    />
    <Route
      path="ai-tools"
      element={<AIGrowthEnginePage session={session} profile={profile} />}
    />
    <Route
      path="chatbot"
      element={<AIAssistantPage session={session} profile={profile} />}
    />
    <Route
      path="mission-hub"
      element={<MyWebsitePage session={session} profile={profile} />}
    />
    <Route
      path="knowledge"
      element={<GraphPage session={session} profile={profile} />}
    />

    <Route
      path="payments"
      element={<PaymentsPage session={session} profile={profile} />}
    />
    <Route
      path="profile"
      element={<ProfilePage session={session} profile={profile} />}
    />
    <Route
      path="subscription"
      element={<NPOSubscription session={session} profile={profile} />}
    />
    <Route
      path="mission-hub/setup"
      element={<NPOMicrosite session={session} profile={profile} />}
    />
    <Route
      path="mission-hub/content"
      element={<MissionHubContentPage session={session} profile={profile} />}
    />

    <Route
      path="knowledge/my-drive"
      element={<MyDrive session={session} profile={profile} />}
    />
    <Route
      path="knowledge/graph"
      element={<GraphPage session={session} profile={profile} />}
    />
    <Route
      path="settings"
      element={<SettingsPage session={session} profile={profile} />}
    />

    <Route
      path="ai-engine/fundraising-and-grants"
      element={<FundraisingAndGrantsPage session={session} profile={profile} />}
    />

    <Route
      path="ai-engine/volunteer-engagement"
      element={<VolunteerEngagementPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/volunteer-engagement/recruitment-emails"
      element={
        <VolunteerRecruitmentEmailPage session={session} profile={profile} />
      }
    />
    <Route
      path="ai-engine/volunteer-engagement/thank-you-emails"
      element={
        <VolunteerThankYouEmailPage session={session} profile={profile} />
      }
    />
    <Route
      path="ai-engine/volunteer-engagement/promotion-images"
      element={
        <CommunityEventPromotionImagesPage
          session={session}
          profile={profile}
        />
      }
    />
    <Route
      path="ai-engine/volunteer-engagement/volunteer-spotlights"
      element={<VolunteerRecognitionPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/volunteer-engagement/automated-reminders"
      element={<VolunteerEngagementPage session={session} profile={profile} />}
    />

    <Route
      path="ai-engine/marketing-outreach"
      element={<MarketingOutreachPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/marketing-outreach/social-posts"
      element={<SocialMediaPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/marketing-outreach/corporate-pitches"
      element={<WriteEmailPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/marketing-outreach/newsletters"
      element={<SocialMediaPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/marketing-outreach/promotion-materials"
      element={<BrainstormIdeasPage session={session} profile={profile} />}
    />

    <Route
      path="ai-engine/impact-reports-and-analytics"
      element={
        <ImpactReportsAndAnalyticsPage session={session} profile={profile} />
      }
    />

    <Route
      path="ai-engine/generate-image"
      element={<GenerateImagePage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/write-email"
      element={<WriteEmailPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/social"
      element={<SocialMediaPage session={session} profile={profile} />}
    />
    <Route
      path="ai-engine/brainstorm"
      element={<BrainstormIdeasPage session={session} profile={profile} />}
    />
    <Route
      path="chatbot/setup"
      element={<ChatPage session={session} profile={profile} />}
    />
    <Route
      path="chatbot/leads"
      element={<ChatLeadsPage session={session} profile={profile} />}
    />
  </Routes>
);

const CorporateDashboardContent = ({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) => (
  <Routes>
    <Route
      index
      element={<DashboardOverview session={session} profile={profile} />}
    />
    <Route
      path="users"
      element={<ManageUsers session={session} profile={profile} />}
    />
    <Route
      path="events"
      element={<Events session={session} profile={profile} />}
    />
    <Route
      path="events/:id"
      element={<EventDetails session={session} profile={profile} />}
    />
    <Route
      path="profile"
      element={<ProfilePage session={session} profile={profile} />}
    />
    <Route
      path="hosts"
      element={<HostsPage session={session} profile={profile} />}
    />
    <Route
      path="cora"
      element={<AskCora session={session} profile={profile} />}
    />
    <Route
      path="campaigns"
      element={<CorporateCampaignsPage session={session} profile={profile} />}
    />
    <Route
      path="campaigns/new"
      element={
        <CorporateNewCampaignsPage session={session} profile={profile} />
      }
    />
    <Route
      path="campaigns/:campaignId"
      element={
        <CorporateViewCampaignsPage session={session} profile={profile} />
      }
    />
    <Route
      path="campaigns/:campaignId/edit"
      element={
        <CorporateEditCampaignsPage session={session} profile={profile} />
      }
    />
  </Routes>
);

export default function UserDashboard({
  profile,
  session,
}: {
  profile: any | null;
  session: any | null;
}) {
  const renderDashboardContent = () => {
    const userType =
      profile?.user_type || session?.user?.user_metadata?.user_type;
    switch (userType) {
      case "company":
        return {
          content: (
            <CorporateDashboardContent session={session} profile={profile} />
          ),
          sidebar: <CorporateSidebar />,
        };
      case "nonprofit":
        return {
          content: <NPODashboardContent session={session} profile={profile} />,
          sidebar: <NPOSidebar />,
        };
      case "host":
        return {
          content: <HostDashboardContent session={session} profile={profile} />,
          sidebar: <HostSidebar />,
        };
      case "anon":
        return {
          content: <AnonDashboardContent session={session} profile={profile} />,
          sidebar: <AnonSidebar />,
        };
      default:
        console.log("No matching user type found, redirecting to login ");
        return null;
    }
  };

  const dashboard = renderDashboardContent();

  if (!dashboard) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <div className="relative w-24 h-24 mb-8">
          {/* Animated circles */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 rounded-full border-4 border-amber-200 opacity-30 animate-ping"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full border-4 border-amber-300 opacity-50 animate-pulse"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-amber-400 animate-bounce"></div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-amber-700 mb-2">
          Secure authentication in process...
        </h2>

        <div className="text-amber-600 text-center max-w-md mb-6">
          We're preparing your personalized experience. This will just take a
          moment...
        </div>

        <div className="flex space-x-2 justify-center items-center">
          <div
            className="h-2 w-2 bg-amber-600 rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></div>
          <div
            className="h-2 w-2 bg-amber-600 rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          ></div>
          <div
            className="h-2 w-2 bg-amber-600 rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Dashboard"
        description="Manage your volunteer opportunities, events, and account settings."
      />
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex">
          <SidebarProvider>
            {dashboard.sidebar}
            <main className="flex-1 flex flex-col min-h-0 bg-gray-50">
              <div className="flex-1 p-8 overflow-y-auto">
                {dashboard.content}
              </div>
            </main>
          </SidebarProvider>
        </div>
      </div>
    </>
  );
}
